import React, { useCallback, useEffect, useState } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';

import NvFroala from 'froala/components/nv-froala';
import { FroalaViewMode, ToolbarOptions, UploadType } from 'froala/helpers/nv-froala-constants';
import { SanitizationLevel } from 'froala/helpers/sanitizer';

import { useAppDispatch } from 'redux/store';
import { getCurrentUser } from 'redux/selectors/users';
import { TranslationInfo } from 'redux/schemas/models/edit-translations';
import { AccordionLectureComponentData, AccordionSectionLectureComponent, ComponentType } from 'redux/schemas/models/lecture-component';
import { closeEditTranslationComponent, translationUpdate } from 'redux/actions/edit-translations';

import { white } from 'styles/global_defaults/colors';
import { doubleSpacing, halfSpacing, largeSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { getLectureComponent } from 'redux/selectors/lecture-components';


type EditableProps = {
  editInfo: TranslationInfo,
  containerClass: string,
  onSaveRef: (fn: () => void) => void,
  setSavingDisabled: (boolean) => void,
};

const EditableAccordionSections = ({
  editInfo,
  containerClass,
  onSaveRef,
  setSavingDisabled,
}: EditableProps) => {
  const lectureComponent = useSelector((state) => (
    getLectureComponent(state, editInfo.id)
  )) as AccordionLectureComponentData;
  const [accordionSections, setAccordionSections] = useState(lectureComponent.accordionSections);
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useAppDispatch();

  const save = useCallback(() => {
    const { id } = editInfo;
    const attributes = [];
    accordionSections.forEach((section, index) => {
      const fields = ['header', 'content'];
      fields.forEach(field => {
        if (section[field] !== lectureComponent.accordionSections[index][field]) {
          attributes.push({
            translationId: section.translationIds[field],
            name: field,
            text: section[field],
            elementType: ComponentType.ACCORDION_SECTION,
            elementId: index,
          });
        }
      });
    });
    const payload = {
      id,
      attributes,
    };
    dispatch(translationUpdate(payload)).then(() => {
      dispatch(closeEditTranslationComponent());
    });
  }, [accordionSections, currentUser.translationPreferenceLanguage, dispatch, editInfo]);

  useEffect(() => {
    onSaveRef(() => save);
  }, [onSaveRef, save]);

  useEffect(() => {
    if (lectureComponent.accordionSections !== accordionSections) {
      setSavingDisabled(false);
    }
    return (() => {
      setSavingDisabled(true);
    });
  }, [accordionSections]);

  const updateSection = (currentSection) => {
    const sectionIndex = accordionSections.findIndex(section => section.id === currentSection.id);
    const newAccordionSections = [...accordionSections];
    newAccordionSections[sectionIndex] = currentSection;
    setAccordionSections(newAccordionSections);
  };

  const simpleTextStyles = css`
    width: 100%;
    margin: 0 ${doubleSpacing}px;
    .bs4-form-control {
      background: ${white};
    }
  `;
  return (
    <div css={simpleTextStyles} className='accordion-sections'>
      {
        accordionSections?.map((section, index) => (
          <EditableAccordionSection
            key={section.id}
            section={section}
            index={index}
            updateSection={updateSection}
            containerClass={containerClass}
          />
        ))
      }
    </div>
  );
};

const EditableAccordionSection = ({
  section,
  index,
  updateSection,
  containerClass,
}: {
  section: AccordionSectionLectureComponent,
  index: number,
  updateSection: (section: AccordionSectionLectureComponent) => void,
  containerClass: string,
}) => {
  const sectionStyles = css`
    .tag {
      margin-bottom: ${halfSpacing}px;
      padding: 3px 12px 3px;
      border-radius: ${halfSpacing}px;
      height: ${standardSpacing}px;
      width: fit-content;
    }
    .accordion-section-header-froala {
      margin-bottom: ${standardSpacing}px;
      .fr-element {
        align-content: center;
        p {
          margin: 0;
        }
      }
      ${section.viewOptions.headerBackgroundColor
        && css`background-color: ${section.viewOptions.headerBackgroundColor};
        .fr-box.nv-froala-editor:hover, .nv-froala-editor:not(.no-background):hover .fr-wrapper:not(.show-placeholder) .fr-element {
          background: ${section.viewOptions.headerBackgroundColor};
        }
      `}
    }
    .accordion-section-content-froala {
      ${section.viewOptions.textBackgroundColor
        && css`background-color: ${section.viewOptions.textBackgroundColor};
        .fr-box.nv-froala-editor:hover, .nv-froala-editor:not(.no-background):hover .fr-wrapper:not(.show-placeholder) .fr-element {
          background: ${section.viewOptions.textBackgroundColor};
        }
      `}
    }
    .separator {
      margin: ${largeSpacing}px 0 ${largeSpacing}px;
    }
  `;
  return (
    <div className='accordion-section' css={sectionStyles}>
      {
        index > 0
        && (
          <hr className='separator bg-gray-4' />
        )
      }
      <div className='tag bg-gray-6 text-small font-weight-bolder'>
        { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.SECTION_TITLE() }
      </div>
      <NvFroala
        className='accordion-section-header-froala'
        preset={FroalaViewMode.NORMAL}
        sanitizationLevel={SanitizationLevel.BASIC}
        toolbarButtons={[
          ToolbarOptions.COLOR,
          ToolbarOptions.BOLD,
          ToolbarOptions.ITALIC,
          ToolbarOptions.UNDERLINE,
          ToolbarOptions.PARAGRAPH_STYLE,
        ]}
        minHeight={65}
        uploadType={UploadType.NONE}
        value={section.header}
        onChange={(newContent) => updateSection({ ...section, header: newContent })}
        colorPickerContainer={containerClass}
      />
      <div className='tag bg-gray-6 text-small font-weight-bolder'>
        { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.SECTION_CONTENT() }
      </div>
      <NvFroala
        className='accordion-section-content-froala'
        preset={FroalaViewMode.NORMAL}
        sanitizationLevel={SanitizationLevel.BASIC}
        toolbarButtons={[
          ToolbarOptions.COLOR,
          ToolbarOptions.BOLD,
          ToolbarOptions.ITALIC,
          ToolbarOptions.UNDERLINE,
          ToolbarOptions.FORMAT_UL,
          ToolbarOptions.FORMAT_OL,
          ToolbarOptions.PARAGRAPH_STYLE,
          ToolbarOptions.ALIGN,
          ToolbarOptions.INSERT_LINK,
        ]}
        minHeight={65}
        uploadType={UploadType.ALL}
        value={section.content}
        onChange={(newContent) => updateSection({ ...section, content: newContent })}
        colorPickerContainer={containerClass}
      />
    </div>
  );
};

export default EditableAccordionSections;
